import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import * as React from 'react';
import { IconButton, LinearProgress, Tooltip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { isMobileSafari } from 'react-device-detect';
import RestHttpClient from '../../../../common/RestHttpClient';
import { HeadCell } from '../../../../common/table/EnhancedTableHead';
import { EnhancedTable } from '../../../../common/table/EnhancedTable';
import { Icons } from '../../../../component/icons/Icons';
import PDFPreviewDialog, { PdfPreview } from '../../../../component/PDFPreviewDialog';
import { DateTimeFormatter } from '../../../../common/formatter/DateTimeFormatter';
import { PrintedDocumentView } from '../../../../generated/ApiClient';

interface Props {
  dealId: number;
}

interface PrintedDocRow {
  id: string;
  documentName: string;
  createdAt: string;
  createdBy: string;
  icon: JSX.Element;
}

export function EditDealPrintedDocuments({ dealId }: Props) {
  const { t } = useTranslation();

  const [docLoading, setDocLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [docs, setDocs] = useState<PrintedDocumentView[]>([]);
  const [openDocFile, setOpenDocFile] = useState<PdfPreview | undefined>();

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        const { data } = await RestHttpClient.getPrintedDocuments(dealId);
        setDocs(data);
      } finally {
        setLoading(false);
      }
    };
    fetchDocuments();
  }, [dealId]);

  const handlePreviewClick = (id: string) => {
    setDocLoading(true);
    let win: Window | undefined;
    if (isMobileSafari) {
      win = window.open(undefined, '_blank') ?? undefined;
    }
    RestHttpClient.getPrintedDocument(dealId, id)
      .then((res) => {
        if (!win) {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64data = reader.result as string;
            setOpenDocFile({ base64: base64data, filename: res.fileName });
          };
          reader.readAsDataURL(res.data);
          return;
        }
        win.location.href = window.webkitURL.createObjectURL(res.data);
      })
      .finally(() => {
        setDocLoading(false);
      });
  };

  const handleDownloadClick = (id: string) => {
    setDocLoading(true);
    RestHttpClient.getPrintedDocument(dealId, id)
      .then((res) => {
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(res.data);
        link.download = res.fileName ?? 'download';
        link.click();
      })
      .finally(() => {
        setDocLoading(false);
      });
  };

  const rows: PrintedDocRow[] = docs.map((d) => ({
    id: d.id,
    documentName: d.documentName,
    createdAt: DateTimeFormatter.format(d.createdAt),
    createdBy: d.createdBy,
    icon: (
      <>
        <Tooltip title={t('deals.edit.documents.view')}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handlePreviewClick(d.id);
            }}
          >
            <VisibilityIcon opacity={0.4} color="secondary" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('deals.edit.documents.download')}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadClick(d.id);
            }}
          >
            {Icons.download()}
          </IconButton>
        </Tooltip>
      </>
    ),
  }));

  const headCells: readonly HeadCell<PrintedDocRow>[] = [
    {
      id: 'documentName',
      disablePadding: false,
      label: t('deals.edit.documents.documentType'),
    },
    {
      id: 'createdAt',
      disablePadding: false,
      label: t('deals.edit.documents.printedAt'),
      sortTransform: (value) => DateTimeFormatter.parse(value),
    },
    {
      id: 'createdBy',
      disablePadding: false,
      label: t('deals.edit.documents.printedBy'),
    },
    {
      id: 'icon',
      align: 'right',
      disablePadding: false,
      label: '',
    },
  ];

  return (
    <>
      {docLoading && <LinearProgress />}
      <PDFPreviewDialog file={openDocFile} onClose={() => setOpenDocFile(undefined)} />
      <EnhancedTable
        loading={loading}
        onRowClick={(_, id) => handlePreviewClick(id)}
        rows={rows}
        headCells={headCells}
        hideCheckbox
        variant="endless"
        defaultOrderBy="createdAt"
        defaultOrder="desc"
      />
    </>
  );
}
